import React, { useState, useEffect, useMemo } from 'react';
import { 
  Box, 
  Typography, 
  CircularProgress,
  Pagination,
  TextField,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import SearchIcon from '@mui/icons-material/Search';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { fetchReport, handleError, formatDateForMySQL, formatCurrency, formatDate2 } from '../../utils/lib';
import { useSnackbar } from '../../providers/SnackBarProvider';
import { useNavigate } from 'react-router-dom';
import useNavigationHelperStore from '../../store/navigationHelperStore';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useReportDatePickerLocale } from '../../hooks/useReportDatePickerLocale';

const SpendingsReport = () => {
  const { t } = useTranslation();
  const { datePickerFormat, dayjsPickerLocale } = useReportDatePickerLocale();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const snackbar = useSnackbar();
  const navigate = useNavigate();
  const { setNavigation, state } = useNavigationHelperStore();
  
  // Date filters
  const [startDate, setStartDate] = useState(dayjs());
  const [endDate, setEndDate] = useState(dayjs());
  
  // Load saved search parameters when returning to the page
  useEffect(() => {
    if (state) {
      const { startDate: savedStartDate, endDate: savedEndDate, page: savedPage } = state;
      if (savedStartDate) setStartDate(dayjs(savedStartDate));
      if (savedEndDate) setEndDate(dayjs(savedEndDate));
      if (savedPage) setPage(savedPage);
      handleSearch();
    }
  }, []);

  // Save search parameters when leaving this page
  const saveCurrentState = () => {
    setNavigation('/main/reports', {
      startDate: startDate.toString(),
      endDate: endDate.toString(),
      page,
    });
  };

  const handleSearch = async () => {
    setIsLoading(true);
    try {
      const formattedStartDate = formatDateForMySQL(startDate);
      const formattedEndDate = formatDateForMySQL(endDate);
      
      const response = await fetchReport('SPENDINGS', 1, {
        from: formattedStartDate,
        to: formattedEndDate
      });
      
      setData(response);
      setPage(1);
    } catch (error) {
      handleError(error, snackbar);
    } finally {
      setIsLoading(false);
    }
  };

  const downloadExcel = async () => {
    try {
      const formattedStartDate = formatDateForMySQL(startDate);
      const formattedEndDate = formatDateForMySQL(endDate);
      
      // Call the backend to get the Excel file in base64 format
      const response = await fetchReport('SPENDINGS_XLSX', 1, {
        from: formattedStartDate,
        to: formattedEndDate,
      });
      
      // Validate response structure and status
      if (response) {
        // Convertir base64 a blob
        const byteCharacters = atob(response);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        
        // Crear enlace y descargar
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `gastos_${formattedStartDate}_${formattedEndDate}.xlsx`);
        document.body.appendChild(link);
        link.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);

        snackbar(t('reports.fileDownloadSuccess'), 'success');
      } else {
        throw new Error(t('reports.exportInvalidFormat'));
      }
    } catch (error) {
      handleError(error, snackbar);
    }
  };
  
  const handlePageChange = async (event, value) => {
    setIsLoading(true);
    try {
      const formattedStartDate = formatDateForMySQL(startDate);
      const formattedEndDate = formatDateForMySQL(endDate);
      
      const response = await fetchReport('SPENDINGS', value, {
        from: formattedStartDate,
        to: formattedEndDate
      });
      
      setData(response);
      setPage(value);
    } catch (error) {
      handleError(error, snackbar);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSpendingClick = (spending) => {
    // Save current state before navigating
    saveCurrentState();
    navigate('/main/view-spending', { state: { id: spending.id } });
  };

  // Calculate total of active (non-deleted) spendings
  const activeTotal = useMemo(() => {
    if (!data?.content) return 0;
    return data.content
      .filter(spending => spending.deleted !== 1)
      .reduce((sum, spending) => sum + (spending.amount || 0), 0);
  }, [data]);

  // Count active (non-deleted) spendings for display
  const activeCount = useMemo(() => {
    if (!data?.content) return 0;
    return data.content.filter(spending => spending.deleted !== 1).length;
  }, [data]);

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={dayjsPickerLocale}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <DatePicker
                label={t('common.from')}
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                format={datePickerFormat}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: "outlined"
                  }
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <DatePicker
                label={t('common.to')}
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                format={datePickerFormat}
                minDate={startDate}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: "outlined"
                  }
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <Box display="flex" justifyContent="center" alignItems="center" gap={2}>
                <CloudDownloadIcon 
                  onClick={downloadExcel}
                  sx={{ 
                    fontSize: 28, 
                    cursor: 'pointer',
                    color: isLoading ? 'text.disabled' : 'text.primary'
                  }}
                />
                <SearchIcon 
                  onClick={handleSearch}
                  sx={{ 
                    fontSize: 28, 
                    cursor: 'pointer',
                    color: isLoading ? 'text.disabled' : 'text.primary'
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </LocalizationProvider>
      </Box>
      
      {isLoading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : data ? (
        <>
          {data.content && data.content.length > 0 ? (
            <>
              <Box mb={2}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="h6">
                      {t('reports.spendingsTitle')}{' '}
                      {t('reports.recordsActiveOfTotal', {
                        active: activeCount,
                        total: data.total_results || data.content.length,
                      })}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Paper sx={{ p: 2, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                      <Typography variant="subtitle1">
                        {t('reports.sellsTotal')} {formatCurrency(activeTotal)}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
              
              <TableContainer component={Paper} sx={{ mb: 2 }}>
                <Table sx={{ minWidth: 350 }} size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('common.date')}</TableCell>
                      <TableCell>{t('common.user')}</TableCell>
                      <TableCell>{t('reports.paymentType')}</TableCell>
                      <TableCell>{t('common.motive')}</TableCell>
                      <TableCell align="right">{t('common.total')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.content.map((spending) => {
                      const isDeleted = spending.deleted === 1;
                      return (
                        <TableRow 
                          key={spending.id}
                          hover
                          onClick={() => handleSpendingClick(spending)}
                          sx={{ 
                            '&:hover': { cursor: 'pointer' },
                            ...(isDeleted && { 
                              backgroundColor: 'action.disabledBackground',
                              textDecoration: 'line-through',
                              opacity: 0.7
                            })
                          }}
                        >
                          <TableCell sx={isDeleted ? { textDecoration: 'line-through' } : {}}>
                            {formatDate2(spending.date)}
                          </TableCell>
                          <TableCell sx={isDeleted ? { textDecoration: 'line-through' } : {}}>
                            {spending.user?.name || '-'}
                          </TableCell>
                          <TableCell sx={isDeleted ? { textDecoration: 'line-through' } : {}}>
                            {spending.payment_type || '-'}
                            {isDeleted && ` ${t('common.cancelledLabel')}`}
                          </TableCell>
                          <TableCell sx={isDeleted ? { textDecoration: 'line-through' } : {}}>
                            {spending.reason || '-'}
                          </TableCell>
                          <TableCell align="right" sx={isDeleted ? { textDecoration: 'line-through' } : {}}>
                            {formatCurrency(spending.amount)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {data.total_pages && data.total_pages > 1 && (
                <Box display="flex" justifyContent="center" mt={2}>
                  <Pagination
                    count={data.total_pages}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                  />
                </Box>
              )}
            </>
          ) : (
            <Box textAlign="center" p={3}>
              <Typography variant="body1">{t('reports.noSpendings')}</Typography>
            </Box>
          )}
        </>
      ) : (
        <Box textAlign="center" p={3}>
          <Typography variant="body1">{t('reports.selectDateRange')}</Typography>
        </Box>
      )}
    </Box>
  );
};

export default SpendingsReport; 